import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WeChatModal from './WeChatModal';

// Import news images
import event202608Banner from '../assets/news/Aug2026/event1.webp';
import event202608BannerMobile from '../assets/news/Aug2026/event1-mobile.webp';
import news1 from '../assets/news/Oct2025/news1.webp';
import news2 from '../assets/news/Oct2025/news2.webp';
import news3 from '../assets/news/Oct2025/news3.webp';
import news4 from '../assets/news/Oct2025/news4.webp';
import news5 from '../assets/news/Oct2025/news5.webp';
import news6 from '../assets/news/Oct2025/news6.webp';
import news7 from '../assets/news/Oct2025/news7.webp';
import news8 from '../assets/news/Oct2025/news8.webp';
import news9 from '../assets/news/Oct2025/news9.webp';
import news10 from '../assets/news/Oct2025/news10.webp';

// Image assets for each event, in the same order as the `events` array in i18n
const eventImageAssets = [
  [event202608Banner],
  [news1, news2, news3, news4, news5, news6, news7, news8, news9, news10],
];

// Mobile-specific overrides, keyed by event index → image index
const eventImageAssetsMobile: Record<number, Record<number, string>> = {
  0: { 0: event202608BannerMobile },
};

interface NewsEventContent {
  title: string;
  date: string;
  badge: string;
  images: { alt: string }[];
  content: {
    intro: string;
    paragraph1: string;
    eventDetails?: {
      title: string;
      bullets: { text: string; indent?: boolean }[];
      groups: { heading: string; items: string[] }[];
    };
    highlightBox1?: { title: string; content: string };
    paragraph2?: string;
    paragraph3?: string;
    highlightBox2?: { title: string; content: string };
    quote?: { text: string; author: string };
    conclusion: string;
    pricing?: {
      title: string;
      items: string[];
      benefitsTitle: string;
      benefits: string[];
      note?: string;
    };
    notice?: string;
    contact?: {
      title: string;
      methods: {
        id?: string;
        label: string;
        value: string;
        link?: string;
        isExternal?: boolean;
      }[];
    };
    disclaimer?: string;
    reference?: { text: string; url: string };
  };
}

export default function NewsSection() {
  const { t } = useTranslation('news');
  const [currentEvent, setCurrentEvent] = useState(0);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isWeChatModalOpen, setIsWeChatModalOpen] = useState(false);

  const events = t('events', { returnObjects: true }) as NewsEventContent[];
  const eventData = events[currentEvent];

  const newsImages = useMemo(
    () =>
      eventImageAssets[currentEvent].map((url, index) => ({
        id: index,
        url,
        mobileUrl: eventImageAssetsMobile[currentEvent]?.[index],
        alt: eventData.images[index]?.alt ?? '',
      })),
    [currentEvent, eventData]
  );

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % newsImages.length);
  }, [newsImages.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(
      (prev) => (prev - 1 + newsImages.length) % newsImages.length
    );
  }, [newsImages.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const nextEvent = useCallback(() => {
    setCurrentEvent((prev) => (prev + 1) % events.length);
    setCurrentSlide(0);
  }, [events.length]);

  const prevEvent = useCallback(() => {
    setCurrentEvent((prev) => (prev - 1 + events.length) % events.length);
    setCurrentSlide(0);
  }, [events.length]);

  return (
    <>
      <section id='news' className='py-32 bg-gray-100'>
      <div className='max-w-7xl mx-auto px-6'>
        <h2 className='text-4xl md:text-6xl font-bold text-center text-gray-900 mb-4'>
          {t('sectionTitle')}
        </h2>
        <div className='gold-underline'></div>

        {/* Event Navigation */}
        <div className='max-w-4xl mx-auto flex items-center justify-center gap-4 mb-6'>
          <button
            onClick={prevEvent}
            className='w-10 h-10 hexagon bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center transition-all duration-300 hover:scale-110'
            aria-label={t('aria.previousEvent', { ns: 'common' })}
          >
            <svg
              className='w-5 h-5 text-gray-900'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='3'
                d='M15 19l-7-7 7-7'
              />
            </svg>
          </button>
          <span className='text-sm font-semibold text-gray-600'>
            {eventData.date}
          </span>
          <button
            onClick={nextEvent}
            className='w-10 h-10 hexagon bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center transition-all duration-300 hover:scale-110'
            aria-label={t('aria.nextEvent', { ns: 'common' })}
          >
            <svg
              className='w-5 h-5 text-gray-900'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth='3'
                d='M9 5l7 7-7 7'
              />
            </svg>
          </button>
        </div>

        {/* Featured News Article */}
        <div className='max-w-4xl mx-auto'>
          <div className='card overflow-hidden'>
            {/* Image Slider */}
            <div className='relative h-96'>
              <div className='hero-slider relative w-full h-full'>
                {newsImages.map((image, index) => (
                  <div
                    key={image.id}
                    className={`hero-slide ${
                      index === currentSlide ? 'active' : ''
                    }`}
                  >
                    <picture>
                      {image.mobileUrl && (
                        <source
                          media='(max-width: 767px)'
                          srcSet={image.mobileUrl}
                        />
                      )}
                      <img
                        src={image.url}
                        alt={image.alt}
                        className='w-full h-full object-cover'
                      />
                    </picture>
                  </div>
                ))}
              </div>

              {/* Slider Controls */}
              {newsImages.length > 1 && (
                <>
                  <button
                    onClick={prevSlide}
                    className='slider-control prev absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 hexagon bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center transition-all duration-300 hover:scale-110'
                    aria-label={t('aria.previousImage', { ns: 'common' })}
                  >
                    <svg
                      className='w-6 h-6 text-gray-900'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='3'
                        d='M15 19l-7-7 7-7'
                      />
                    </svg>
                  </button>
                  <button
                    onClick={nextSlide}
                    className='slider-control next absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 hexagon bg-yellow-400 hover:bg-yellow-500 flex items-center justify-center transition-all duration-300 hover:scale-110'
                    aria-label={t('aria.nextImage', { ns: 'common' })}
                  >
                    <svg
                      className='w-6 h-6 text-gray-900'
                      fill='none'
                      stroke='currentColor'
                      viewBox='0 0 24 24'
                    >
                      <path
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='3'
                        d='M9 5l7 7-7 7'
                      />
                    </svg>
                  </button>

                  {/* Slider Indicators */}
                  <div className='absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2'>
                    {newsImages.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className={`slider-indicator w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          index === currentSlide
                            ? 'active bg-yellow-400 w-8'
                            : 'bg-white bg-opacity-50 hover:bg-opacity-75'
                        }`}
                        aria-label={t('buttons.goToSlide', {
                          number: index + 1,
                          ns: 'common',
                        })}
                      />
                    ))}
                  </div>
                </>
              )}

              <div className='badge badge-gold absolute left-6 -bottom-3'>
                {eventData.badge}
              </div>
            </div>
            <div className='p-8 md:p-12'>
              <h3 className='text-3xl md:text-4xl font-bold text-gray-900 mb-4'>
                {eventData.title}
              </h3>
              <div className='text-sm text-gray-600 mb-6'>
                {eventData.date}
              </div>

              <div className='prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-6'>
                <p className='text-xl font-semibold text-gray-900'>
                  {eventData.content.intro}
                </p>

                {eventData.content.paragraph1 && (
                  <p className='whitespace-pre-line'>
                    {eventData.content.paragraph1}
                  </p>
                )}

                {eventData.content.eventDetails && (
                  <div className='bg-gray-900 text-white p-6 rounded-lg my-8'>
                    <h4 className='font-bold mb-4 text-xl text-yellow-400'>
                      {eventData.content.eventDetails.title}
                    </h4>
                    <ul className='space-y-2'>
                      {eventData.content.eventDetails.bullets.map(
                        (bullet, index) =>
                          bullet.indent ? (
                            <li key={index} className='pl-6 text-gray-300'>
                              {bullet.text}
                            </li>
                          ) : (
                            <li key={index} className='flex gap-2'>
                              <span className='text-yellow-400'>•</span>
                              <span>{bullet.text}</span>
                            </li>
                          )
                      )}
                    </ul>
                    {eventData.content.eventDetails.groups.map(
                      (group, index) => (
                        <div key={index} className='mt-5'>
                          <p className='font-semibold text-yellow-400 mb-2'>
                            {group.heading}
                          </p>
                          <ul className='space-y-1'>
                            {group.items.map((item, itemIndex) => (
                              <li key={itemIndex} className='flex gap-2'>
                                <span className='text-yellow-400'>•</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )
                    )}
                  </div>
                )}

                {eventData.content.highlightBox1?.content && (
                  <div className='bg-yellow-50 border-l-4 border-yellow-400 p-6 my-8'>
                    <h4 className='font-bold text-gray-900 mb-3 text-xl'>
                      {eventData.content.highlightBox1.title}
                    </h4>
                    <p className='whitespace-pre-line'>
                      {eventData.content.highlightBox1.content}
                    </p>
                  </div>
                )}

                {eventData.content.paragraph2 && (
                  <p>{eventData.content.paragraph2}</p>
                )}

                {eventData.content.paragraph3 && (
                  <p>{eventData.content.paragraph3}</p>
                )}

                {eventData.content.highlightBox2?.content && (
                  <div className='bg-gray-900 text-white p-6 rounded-lg my-8'>
                    <h4 className='font-bold mb-3 text-xl text-yellow-400'>
                      {eventData.content.highlightBox2.title}
                    </h4>
                    <p className='whitespace-pre-line'>
                      {eventData.content.highlightBox2.content}
                    </p>
                  </div>
                )}

                {eventData.content.quote && (
                  <blockquote className='border-l-4 border-gray-900 pl-6 my-8 italic text-xl text-gray-800 whitespace-pre-line'>
                    <p className='mb-2'>{eventData.content.quote.text}</p>
                    <footer className='text-base not-italic font-semibold text-gray-900 mt-4'>
                      — {eventData.content.quote.author}
                    </footer>
                  </blockquote>
                )}
                <p className='text-gray-900 whitespace-pre-line'>
                  {eventData.content.conclusion}
                </p>

                {eventData.content.pricing && (
                  <div>
                    <p className='font-semibold text-gray-900 mb-2'>
                      {eventData.content.pricing.title}
                    </p>
                    <ul className='space-y-1 mb-4'>
                      {eventData.content.pricing.items.map((item, index) => (
                        <li key={index} className='flex gap-2'>
                          <span className='text-yellow-500'>•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <p className='font-semibold text-gray-900 mb-2'>
                      {eventData.content.pricing.benefitsTitle}
                    </p>
                    <ul className='space-y-1'>
                      {eventData.content.pricing.benefits.map(
                        (benefit, index) => (
                          <li key={index} className='flex gap-2'>
                            <span className='text-yellow-500'>
                              {index + 1})
                            </span>
                            <span>{benefit}</span>
                          </li>
                        )
                      )}
                    </ul>
                    {eventData.content.pricing.note && (
                      <p className='font-semibold text-gray-900 mt-4'>
                        {eventData.content.pricing.note}
                      </p>
                    )}
                  </div>
                )}

                {eventData.content.notice && (
                  <p className='text-gray-700'>{eventData.content.notice}</p>
                )}

                {eventData.content.contact && (
                  <div>
                    <p className='font-semibold text-gray-900 mb-3'>
                      {eventData.content.contact.title}
                    </p>
                    <ul className='space-y-2'>
                      {eventData.content.contact.methods.map((method) =>
                        method.id === 'wechat' ? (
                          <li key={method.label}>
                            <button
                              type='button'
                              onClick={() => setIsWeChatModalOpen(true)}
                              className='inline-flex items-center gap-1.5 underline decoration-gray-400 hover:text-yellow-600 hover:decoration-yellow-600 transition-colors duration-300'
                            >
                              {method.label}：{method.value}
                            </button>
                          </li>
                        ) : (
                          <li key={method.label}>
                            {method.link ? (
                              <a
                                href={method.link}
                                target={
                                  method.isExternal ? '_blank' : undefined
                                }
                                rel={
                                  method.isExternal
                                    ? 'noopener noreferrer'
                                    : undefined
                                }
                                className='inline-flex items-center gap-1.5 underline decoration-gray-400 hover:text-yellow-600 hover:decoration-yellow-600 transition-colors duration-300 group'
                              >
                                <span>
                                  {method.label}：{method.value}
                                </span>
                                <svg
                                  className='w-4 h-4 transition-transform duration-300 group-hover:translate-x-1'
                                  fill='none'
                                  stroke='currentColor'
                                  viewBox='0 0 24 24'
                                >
                                  <path
                                    strokeLinecap='round'
                                    strokeLinejoin='round'
                                    strokeWidth='2'
                                    d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                  />
                                </svg>
                              </a>
                            ) : (
                              <span>
                                {method.label}：{method.value}
                              </span>
                            )}
                          </li>
                        )
                      )}
                    </ul>
                  </div>
                )}

                {eventData.content.disclaimer && (
                  <p className='text-sm text-gray-500 italic'>
                    {eventData.content.disclaimer}
                  </p>
                )}

                {/* Reference Link */}
                {eventData.content.reference && (
                  <div className='mt-8 pt-6 border-t border-gray-300'>
                    <a
                      href={eventData.content.reference.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='inline-flex items-center gap-2 text-yellow-500 hover:text-yellow-600 font-semibold transition-colors duration-300 group'
                    >
                      <span>{eventData.content.reference.text}</span>
                      <svg
                        className='w-5 h-5 transition-transform duration-300 group-hover:translate-x-1'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth='2'
                          d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                        />
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </section>

      <WeChatModal
        isOpen={isWeChatModalOpen}
        onClose={() => setIsWeChatModalOpen(false)}
      />
    </>
  );
}
